import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AbstractControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { filter, Observable } from 'rxjs';
import { EditableField, EditableFieldSection } from '../../../models/ui/editable-field-section.model';
import { EditableFieldNode, EditableFieldType } from '../../../models/ui/editable-field-node.model';
import { BaseComponent } from '../../base.component';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'lex-add-case-field',
    templateUrl: './add-case-field.component.html',
    styleUrls: ['./add-case-field.component.scss'],
    standalone: false
})
export class AddCaseFieldComponent extends BaseComponent implements OnInit {
	public parentSectionId: string = '';
	public fields: EditableFieldSection[] = [];
	public fieldForm: UntypedFormGroup;
	public isNewSection: boolean = false;

	private fields$: Observable<EditableFieldSection[]>;

	public fieldTypes: Array<{ value: EditableFieldType, displayName: string }> = [
		{ value: 'input', displayName: 'Text Field' },
		{ value: 'checkbox', displayName: 'Checkbox Field' }
	];

	constructor(private dialogRef: MatDialogRef<AddCaseFieldComponent>,
				private fb: UntypedFormBuilder,
				@Inject(MAT_DIALOG_DATA) private data) {
		super();
		this.fields$ = data.fields$;
	}

	ngOnInit() {
		this.fields$.pipe(
			filter((x) => !!x),
			takeUntil(this.destroy$)
		).subscribe((data) => {
			this.fields = data;
		});

		this.fieldForm = this.fb.group({
			selectParentSelection: new UntypedFormControl(),
			newParentSection: new UntypedFormControl('', [Validators.required, this.duplicateTitleValidatorFn()]),
			newFieldTitle: new UntypedFormControl('', [Validators.required, this.duplicateTitleValidatorFn()]),
			fieldType: new UntypedFormControl('', [Validators.required])
		});

	}

	public ok() {
		if (this.fieldForm.invalid) {
			this.dialogRef.close(null);
			return;
		}

		const targetFields: EditableField[] = [];

		if (!this.parentSectionId) {
			const newSection = new EditableFieldSection();
			newSection.label = this.fieldForm.get('newParentSection').value;
			targetFields.push(newSection);
		}

		const newField = new EditableFieldNode();
		newField.label = this.fieldForm.get('newFieldTitle').value;
		newField.type = this.fieldForm.get('fieldType').value;
		newField.parentId = this.parentSectionId;
		targetFields.push(newField);

		this.dialogRef.close(targetFields);
	}

	public sectionChanged($event) {
		this.parentSectionId = $event.value;
		this.isNewSection = false;
		this.fieldForm.get('newParentSection').reset();
		this.setValidators();
	}

	public createNewSection() {
		this.parentSectionId = null;
		this.isNewSection = true;
		this.fieldForm.get('selectParentSelection').reset();
		this.setValidators();
	}

	public setValidators() {
		const parentSelectControl = this.fieldForm.get('selectParentSelection');
		const newParentControl = this.fieldForm.get('newParentSection');

		if (!this.parentSectionId) {
			parentSelectControl.setValidators(null);
			newParentControl.setValidators([Validators.required, this.duplicateTitleValidatorFn()]);
		} else {
			parentSelectControl.setValidators([Validators.required]);
			newParentControl.setValidators(null);
		}

		parentSelectControl.updateValueAndValidity();
		newParentControl.updateValueAndValidity();
	}

	public duplicateTitleValidatorFn(): ValidatorFn {
		return (control: AbstractControl): { [key: string]: any } | null => {
			const forbidden = this.isDuplicateTitle(control.value);
			return forbidden ? { 'duplicateTitle': { value: control.value } } : null;
		};
	}

	public isDuplicateTitle(title: string): boolean {
		const allTitles = this.fields.map(cf => cf.label.toLowerCase());
		return allTitles.includes(title?.toLowerCase());
	}
}
